import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  handleNotificationAction,
  Notification,
} from '../utils/notifications';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  handleAction: (id: string, action: 'accept' | 'decline') => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only fetch when we have a coherent session. A stale `access_token` alone (e.g. on /login)
  // must not hit /notifications — that produced 401 noise and confusion.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const engineerSignedInArea =
    path.startsWith('/engineer') &&
    !path.startsWith('/engineer/login') &&
    !path.startsWith('/engineer/setup');
  const hasEngineerToken = Boolean(
    typeof window !== 'undefined' && localStorage.getItem('engineer_token'),
  );
  const canFetch =
    (isAuthenticated && !!user) || (engineerSignedInArea && hasEngineerToken);

  const refreshNotifications = useCallback(async () => {
    if (!canFetch) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetchNotifications({ limit: 50 });
      const data = Array.isArray((response as any)?.data)
        ? (response as any).data
        : Array.isArray(response as any)
          ? (response as any)
          : [];
      const unread =
        typeof (response as any)?.unreadCount === 'number'
          ? (response as any).unreadCount
          : data.filter((n: Notification) => !n.read).length;
      setNotifications(data);
      setUnreadCount(unread);
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to load notifications');
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [canFetch]);

  const markAsRead = async (id: string) => {
    try {
      if (canFetch) {
        await markNotificationAsRead(id);
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, read: true }
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      setError(err.message || 'Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      if (canFetch) {
        await markAllNotificationsAsRead();
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
    } catch (err: any) {
      console.error('Error marking all notifications as read:', err);
      setError(err.message || 'Failed to mark all notifications as read');
    }
  };

  const handleAction = async (id: string, action: 'accept' | 'decline') => {
    try {
      if (canFetch) {
        await handleNotificationAction(id, action);
      }
      
      // Remove notification from list after action
      setNotifications(prev => prev.filter(notification => notification.id !== id));
      
      // Update unread count if it was unread
      const notification = notifications.find(n => n.id === id);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      console.error('Error handling notification action:', err);
      setError(err.message || 'Failed to handle notification action');
    }
  };

  // Load notifications on mount and auth change
  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  // Refresh notifications every 30 seconds when authenticated
  useEffect(() => {
    if (!canFetch) return;

    const interval = setInterval(refreshNotifications, 30000);
    return () => clearInterval(interval);
  }, [canFetch, refreshNotifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    handleAction,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
